<template>
  <div :class="['pick-date-box', has_error && 'pick-date-box--error']">
    <time>
      <DatePickerNext
        :value="value.date"
        type="date"
        :disabled="disabled"
        :format="format"
        :placeholder="format"
        :disabled-date="cantPickDate"
        @change="calendarChange"
        :popup-class="'mx-datepicker-main-spec'"
      />
    </time>
    <time>
      <DatePickerNext
        :value="value.time"
        type="time"
        :disabled="disabled || !value.date"
        :format="`h:mm A`"
        :placeholder="`h:mm A`"
        :disabled-time="cantPickTime"
        @open="rememberAmPm"
        @change="timeChange"
        :popup-class="'mx-datepicker-main-spec'"
        :key="timeKey"
      />
    </time>
  </div>
</template>

<script>
import DatePickerNext from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import Button from '@/components/ui/Button.vue';
import moment from 'moment';

export default {
  name: 'DateTimePicker',
  emits: ['update-time', 'update-date'],
  components: {
    DatePickerNext,
    SvgIcon,
    Button,
  },

  computed: {},

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    date: {
      type: [Date, String, Number],
      default: null,
    },

    time: {
      type: [Date, String, Number],
      default: null,
    },

    datetime: {
      type: [Date, String, Number],
      default: null,
    },

    format: {
      type: String,
      default: 'MM/DD/YYYY',
    },

    spec_val: {
      type: String,
      default: '',
    },

    index: {
      type: [String, Number],
      default: null,
    },

    max_date: {
      type: [Date, String, Number],
      default: null,
    },

    min_date: {
      type: [Date, String, Number],
      default: null,
    },
    spec_date: {
      type: Boolean,
      default: false,
    },
    has_error: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      value: {
        date: '',
        time: '',
      },
      date_value: {
        key: 0,
        maxDate: null,
        minDate: null,
      },
      format_time: `h:mm A`,
      key: 0,
      lastAmPm: null, // Track the last AM/PM state
      timeKey: 0,
    };
  },

  watch: {
    max_date: {
      handler(newVal) {
        this.date_value.maxDate = newVal;
      },
      immediate: true
    },
    min_date: {
      handler(newVal) {
        this.date_value.minDate = newVal;
      },
      immediate: true
    }
  },

  methods: {
    rememberAmPm() {
      if (this.value.time) {
        this.lastAmPm = moment(this.value.time).format('A'); // Record current AM/PM
        console.log('this.lastAmPm', this.lastAmPm);
      }
    },

    calendarChange(date) {
      const obj = {};
      this.value.date = date;
      obj.key = this.spec_val;
      obj.index = this.index;
      obj.val = moment(date).format(this.format);
      this.$emit('update-date', obj);
    },

    timeChange(time) {
      let newTime = moment(time);
      const oldTime = moment(this.value.time);

      const oldHour = oldTime.hour();
      const newHour = newTime.hour();

      // Check if user manually toggled AM/PM
      const userToggledAmPm = (oldHour < 12 && newHour >= 12) || (oldHour >= 12 && newHour < 12);

      // If user didn't manually toggle AM/PM, maintain the original AM/PM state
      if (!userToggledAmPm && this.lastAmPm && newTime.format('A') !== this.lastAmPm) {
        if (this.lastAmPm === 'AM' && newTime.hour() >= 12) {
          newTime.subtract(12, 'hours');
        } else if (this.lastAmPm === 'PM' && newTime.hour() < 12) {
          newTime.add(12, 'hours');
        }
      }

      this.value.time = newTime.toDate();
      const obj = {
        key: this.spec_val,
        index: this.index,
        val: newTime.format(this.format_time),
      };

      this.$emit('update-time', obj);

      if (!this.spec_date) {
        this.timeKey += 1; // Force re-render time picker
      }
    },

    cantPickTime(time) {
      if (!this.min_date || !this.value.date) {
        return false;
      }

      const minDate = new Date(this.min_date);
      const selectedDate = new Date(this.value.date);

      // Only disable past times when selected date is today
      const isSameDay =
        selectedDate.getFullYear() === minDate.getFullYear() &&
        selectedDate.getMonth() === minDate.getMonth() &&
        selectedDate.getDate() === minDate.getDate();

      if (!isSameDay) {
        return false;
      }

      // Disable hours less than or equal to current hour (only allow next hour onwards)
      const timeHour = time.getHours();
      const minHour = minDate.getHours();

      return timeHour <= minHour;
    },

    cantPickDate(date) {
      // date_value is now updated via watchers
      let maxDate = this.date_value.maxDate ? new Date(this.date_value.maxDate) : null;
      let minDate = this.date_value.minDate ? new Date(new Date(this.date_value.minDate).getTime()) : null;

      // Set to start of day (00:00:00) for comparison, so current day can be selected
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      if (maxDate) {
        maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      }

      if (minDate) {
        minDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      }

      if (maxDate && minDate) {
        // Check both max and min date
        return dateOnly < minDate || dateOnly > maxDate;
      } else if (maxDate) {
        // Only max date restriction
        return dateOnly > maxDate;
      } else if (minDate) {
        // Only min date restriction
        return dateOnly < minDate;
      } else {
        // No date restriction
        return false;
      }
    },
  },

  mounted() {
    if (this.date && this.datetime) {
      this.value.date = moment(this.datetime).toDate();
    }

    if (this.time && this.datetime) {
      this.value.time = moment(this.datetime).toDate();
    }
  },
};
</script>
<style lang="scss">
.pick-date-box {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;

  .mx-datepicker {
    width: unset !important;
  }

  &.pick-date-box--error {
    .mx-input-wrapper input,
    .mx-datepicker input {
      border-color: var(--alert) !important;
    }
  }
}
</style>
